import { jsx as _jsx, jsxs as _jsxs } from "react/jsx-runtime";
import * as React from "react";
import * as OAuth from "./oauth";
import * as Config from "configuration";
import * as Query from "react-query-carnet-rouge";
import * as Storage from "storage";
import * as Equals from "equals";
import ms from "ms";
import Loader from "@valraiso-esf/esf-ui/es/loader-circle";
import { AccessDeniedError } from "./access-denied-error";
import css from "./authorization.module.css";
const isProd = Config.environment() === "production";
const TOKEN_EXPIRATION_THRESHOLD = ms("5m");
const LOCALSTORAGE_AUTHORIZATION_TOKEN_KEY = "authorization_token";
export function tokenFromStorage() {
    const data = localStorage.getItem(LOCALSTORAGE_AUTHORIZATION_TOKEN_KEY);
    if (!data) {
        return undefined;
    }
    return JSON.parse(data);
}
export function setTokenInStorage(token) {
    const oldValue = localStorage.getItem(LOCALSTORAGE_AUTHORIZATION_TOKEN_KEY);
    const stored = JSON.stringify(token);
    localStorage.setItem(LOCALSTORAGE_AUTHORIZATION_TOKEN_KEY, stored);
    // Pour avertir l'onglet courant qu'un event storage est survenu et mettre à jour l'Auth Provider
    // ->Sinon, seuls les autres onglets sont avertis
    window.dispatchEvent(new StorageEvent("storage", {
        key: LOCALSTORAGE_AUTHORIZATION_TOKEN_KEY,
        oldValue,
        newValue: stored,
        storageArea: localStorage,
    }));
}
export function getAccessToken() {
    const token = tokenFromStorage();
    if (!token) {
        return undefined;
    }
    return constructAccessToken(token);
}
export function constructAccessToken(token) {
    return `${token.token_type} ${token.access_token}`.trim();
}
const LOCALSTORAGE_AUTHORIZATION_USER_KEY = "authorization_user";
export function userFromStorage() {
    const data = localStorage.getItem(LOCALSTORAGE_AUTHORIZATION_USER_KEY);
    if (!data) {
        return undefined;
    }
    return JSON.parse(data);
}
export function setUserInStorage(user) {
    const oldValue = localStorage.getItem(LOCALSTORAGE_AUTHORIZATION_USER_KEY);
    const stored = JSON.stringify(user);
    localStorage.setItem(LOCALSTORAGE_AUTHORIZATION_USER_KEY, stored);
    // Pour avertir l'onglet courant qu'un event storage est survenu et mettre à jour l'Auth Provider
    // ->Sinon, seuls les autres onglets sont avertis
    window.dispatchEvent(new StorageEvent("storage", {
        key: LOCALSTORAGE_AUTHORIZATION_USER_KEY,
        oldValue,
        newValue: stored,
        storageArea: localStorage,
    }));
}
const skipParams = ["code", "state", "scope", "session_state"];
function redirectToCleanURL(user) {
    const { origin, pathname, search } = window.location;
    const searchParams = new URLSearchParams(search);
    for (const p of skipParams) {
        searchParams.delete(p);
    }
    const returnTo = sessionStorage.getItem("return_to");
    if (returnTo)
        sessionStorage.removeItem("return_to");
    const pathnameToReturn = returnTo || pathname;
    const params = searchParams.toString();
    const root = origin +
        (user && user.eligible === false
            ? "/commande-carte-syndicale"
            : pathnameToReturn);
    const cleanedURL = params.length > 0 ? root + "?" + params : root;
    document.location.href = cleanedURL;
}
async function handleReturnFromCodeExchange(code, state) {
    const pkceState = sessionStorage.getItem("pkce_state");
    const pkceVerifier = sessionStorage.getItem("pkce_code_verifier");
    sessionStorage.removeItem("pkce_code_verifier");
    sessionStorage.removeItem("pkce_state");
    if (pkceState !== state || pkceVerifier === null) {
        return redirectToCleanURL();
    }
    const data = await OAuth.exchangeCodeForAccessToken(code, pkceVerifier);
    if (!data) {
        return redirectToCleanURL();
    }
    setTokenInStorage(data);
    const user = await getAuthorization(true);
    return redirectToCleanURL(user);
}
const AuthContext = React.createContext({
    token: undefined,
});
export default function AuthProvider({ children }) {
    const queryClient = Query.useQueryClient();
    const [token, setToken] = React.useState(tokenFromStorage());
    const accessToken = token !== undefined ? token.access_token : undefined;
    const [user, setUser] = React.useState(userFromStorage());
    const [checking, setChecking] = React.useState(accessToken !== undefined && false);
    const [previousUser, setPreviousUser] = Storage.useState("localStorage", ["oauth2", "auth-provider", "previous-user"], user);
    if (accessToken && !checking) {
        if (!user) {
            throw new AccessDeniedError("L'accès au Carnet Rouge ne vous a pas encore été délivré");
        }
        if (!hasAccessRole(user)) {
            throw new AccessDeniedError("L'accès au Carnet Rouge ne vous a pas encore été délivré (pas de rôle d'accès)");
        }
    }
    const params = new URLSearchParams(window.location.search);
    const code = params.get("code");
    const state = params.get("state");
    React.useEffect(() => {
        const handleStorage = (event) => {
            if ((!event.storageArea || event.storageArea === localStorage) &&
                (event.key === LOCALSTORAGE_AUTHORIZATION_TOKEN_KEY ||
                    event.key === LOCALSTORAGE_AUTHORIZATION_USER_KEY ||
                    event.key === null)) {
                const tokenFromStorage_ = tokenFromStorage();
                const userFromStorage_ = userFromStorage();
                if (!Equals.deepStrictEqual(tokenFromStorage_, token) ||
                    !Equals.deepStrictEqual(userFromStorage_, user)) {
                    setToken(tokenFromStorage_);
                    setUser(userFromStorage_);
                }
            }
        };
        window.addEventListener("storage", handleStorage);
        return () => {
            window.removeEventListener("storage", handleStorage);
        };
    }, []);
    React.useEffect(() => {
        let timeout;
        const reconnect = async () => {
            try {
                await getAuthorization();
            }
            catch (e) {
                console.error(e);
            }
            setChecking(false);
        };
        if (accessToken) {
            if (checking) {
                reconnect();
            }
            else {
                const decodedAccessToken = OAuth.decodeAccessToken(accessToken);
                const expiresAt = decodedAccessToken.exp * 1000;
                const expiresIn = expiresAt - Date.now();
                const refreshedIn = Math.max(0, expiresIn - TOKEN_EXPIRATION_THRESHOLD);
                timeout = setTimeout(OAuth.refreshAuth, refreshedIn);
            }
        }
        return () => {
            if (timeout !== undefined) {
                clearTimeout(timeout);
            }
        };
    }, [accessToken, checking]);
    React.useEffect(() => {
        const run = async (code, state) => {
            try {
                await handleReturnFromCodeExchange(code, state);
            }
            catch (erreur) {
                console.error(erreur);
                redirectToCleanURL();
            }
        };
        if (code !== null && state !== null) {
            run(code, state);
        }
    }, [code, state]);
    const resetQueryClientIfDifferentUser = React.useCallback(async () => {
        if (!Equals.deepStrictEqual(user, previousUser)) {
            await queryClient.cancelQueries();
            queryClient.clear();
            setPreviousUser(user);
        }
    }, [user, previousUser]);
    React.useEffect(() => {
        resetQueryClientIfDifferentUser();
    }, [resetQueryClientIfDifferentUser]);
    // Sans école : les chemins hors SANS_ECOLE_ALLOWED_PATHS redirigent vers
    // l'accueil (parcours dédié) au lieu d'un écran « accès interdit ».
    const sansEcoleRedirect = accessToken !== undefined &&
        user !== undefined &&
        isSansEcole(user) &&
        !isSansEcoleAllowedPath(window.location.pathname);
    React.useEffect(() => {
        if (sansEcoleRedirect)
            window.location.replace("/");
    }, [sansEcoleRedirect]);
    const context = React.useMemo(() => ({ token: accessToken, user }), [accessToken, user]);
    if ((code !== null && state !== null) || checking || sansEcoleRedirect) {
        return (_jsxs("div", { className: css.outer, children: [_jsx("span", { children: "Connexion en cours..." }), _jsx(Loader, {})] }));
    }
    return (_jsx(AuthContext.Provider, { value: context, children: children }));
}
export function useAuth() {
    const auth = React.useContext(AuthContext);
    const token = tokenFromStorage();
    const accessToken = getAccessToken();
    return React.useMemo(() => ({
        user: auth.user,
        token: accessToken ?? "",
        signed: auth.token !== undefined,
        signin: () => OAuth.signIn(),
        signout: () => OAuth.signOut(token),
    }), [Equals.stableStringify(auth), Equals.stableStringify(token), accessToken]);
}
export function clear() {
    localStorage.removeItem(LOCALSTORAGE_AUTHORIZATION_TOKEN_KEY);
    localStorage.removeItem(LOCALSTORAGE_AUTHORIZATION_USER_KEY);
    localStorage.removeItem("notification_date");
    localStorage.removeItem("notification_read");
    localStorage.removeItem("notification_unread");
    localStorage.removeItem("ecole");
}
export function deconnect(shouldReturnToCurrentPage) {
    const token = tokenFromStorage();
    if (shouldReturnToCurrentPage) {
        sessionStorage.setItem("return_to", window.location.pathname);
    }
    OAuth.signOut(token);
}
async function getAuthorization(tracked = false) {
    const token = getAccessToken();
    if (token) {
        const response = await fetchAuthorization(token, tracked);
        if (response.ok) {
            const user = (await response.json());
            setUserInStorage(user);
            return user;
        }
        else {
            if (response.status === 401) {
                const refreshed = await OAuth.refreshAuth(tracked);
                return refreshed.user;
            }
            else if (response.status === 403) {
                deconnect();
            }
        }
    }
}
export async function fetchAuthorization(accessToken, tracked = false, init) {
    const api = Config.get("apiCarnetRouge");
    let url = `${api}/api/carnet-rouge/authorization?clearCache=true`;
    if (tracked)
        url += "&tracked=true";
    return fetch(url, {
        headers: {
            authorization: accessToken,
            ...init?.headers,
        },
    });
}
const ECOLE_ACCESS_ROLES = new Set([
    // Rôles induits
    "MONITEUR",
    "DIR",
    "DT",
    // Rôles école
    "E_COLLAB_CR",
    "E_ASSDIR_CR",
    "E_COMPTA_CR",
    "E_EXTERNE_CR",
    "E_CM_CR",
]);
const SNMSF_ACCESS_ROLES = new Set([
    // Rôles nationaux
    "S_ADMIN_CR",
    "S_COMITE_CR",
    "S_COLLAB_CR",
    "S_PRESTATAIRE_CR",
    "S_ACCIDENT_CR",
    "S_COMPTA_CR",
].concat(isProd ? [] : ["S_EXTERNE_CR"]));
const ACCESS_ROLES = new Set([...ECOLE_ACCESS_ROLES, ...SNMSF_ACCESS_ROLES]);
function hasAccessRole(user) {
    return intersection(user.roles, ACCESS_ROLES).length > 0;
}
function hasEcoleAccessRole(user) {
    return intersection(user.roles, ECOLE_ACCESS_ROLES).length > 0;
}
// Moniteur pas encore associé à une école : accès restreint aux chemins
// SANS_ECOLE_ALLOWED_PATHS, menu filtré et accueil dédié (le dépôt d'une
// demande de carte syndicale est justement ce qui l'associera à une école).
// Seul le rôle MONITEUR est concerné ; les écoles renforts (noEcolesRenforts)
// ne comptent pas comme une association : seule noEcole fait foi.
export function isSansEcole(user) {
    return !user.noEcole && user.roles.includes("MONITEUR");
}
// Chemins accessibles sans école. "/" ne couvre que l'accueil exactement : le
// match préfixe exige un "/" de séparation, donc "//…" ne matche jamais.
const SANS_ECOLE_ALLOWED_PATHS = ["/", "/carte-syndicale", "/mon-compte"];
export function isSansEcoleAllowedPath(pathname) {
    return SANS_ECOLE_ALLOWED_PATHS.some((path) => pathname === path || pathname.startsWith(`${path}/`));
}
function intersection(a, b) {
    return a.filter((r) => b.has(r));
}
